import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const SENSORY = '#2493A2';
const INTEGRATION = '#57BDA2';

export function StabilityField() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 280;
    const height = 180;
    const cx = width / 2;
    const cy = height / 2;

    let stopped = false;

    // --- Certainty halo (background, initially invisible) ---
    const halo = svg
      .append('circle')
      .attr('cx', cx)
      .attr('cy', cy)
      .attr('r', 0)
      .attr('fill', 'none')
      .attr('stroke', INTEGRATION)
      .attr('stroke-width', 2)
      .attr('opacity', 0);

    const haloGlow = svg
      .append('circle')
      .attr('cx', cx)
      .attr('cy', cy)
      .attr('r', 0)
      .attr('fill', INTEGRATION)
      .attr('opacity', 0);

    // --- Wobbling center dot ---
    const dot = svg
      .append('circle')
      .attr('cx', cx)
      .attr('cy', cy)
      .attr('r', 6)
      .attr('fill', SENSORY);

    // --- Labels ---
    const uncertainLabel = svg
      .append('text')
      .attr('x', cx)
      .attr('y', cy + 50)
      .attr('text-anchor', 'middle')
      .attr('font-size', 12)
      .attr('font-weight', 600)
      .attr('class', 'viz-text')
      .attr('opacity', 0)
      .text('uncertain');

    const certainLabel = svg
      .append('text')
      .attr('x', cx)
      .attr('y', cy + 50)
      .attr('text-anchor', 'middle')
      .attr('font-size', 14)
      .attr('font-weight', 700)
      .attr('class', 'viz-text')
      .attr('opacity', 0)
      .text('certain');

    // --- Wobble animation using requestAnimationFrame ---
    let frame = 0;
    const totalFrames = 180; // ~3 seconds at 60fps
    let animFrameId: number;

    function animate() {
      if (stopped) return;

      const progress = Math.min(frame / totalFrames, 1);
      // Wobble amplitude decreases over time
      const amplitude = 20 * (1 - progress);
      const speed = 0.12;

      const offsetX = Math.sin(frame * speed) * amplitude;
      const offsetY = Math.cos(frame * speed * 1.3) * amplitude * 0.6;

      dot.attr('cx', cx + offsetX).attr('cy', cy + offsetY);

      frame++;

      if (frame <= totalFrames) {
        animFrameId = requestAnimationFrame(animate);
      }
    }

    animFrameId = requestAnimationFrame(animate);

    // Show "uncertain" label quickly
    const t0 = d3.timeout(() => {
      uncertainLabel
        .transition()
        .duration(300)
        .attr('opacity', 0.8);
    }, 300);

    // --- Halo grows as wobble decreases ---
    const t1 = d3.timeout(() => {
      halo
        .transition()
        .duration(1500)
        .ease(d3.easeQuadOut)
        .attr('r', 35)
        .attr('opacity', 0.6);

      haloGlow
        .transition()
        .duration(1500)
        .ease(d3.easeQuadOut)
        .attr('r', 35)
        .attr('opacity', 0.06);
    }, 800);

    // --- Switch label from uncertain to certain ---
    const t2 = d3.timeout(() => {
      uncertainLabel
        .transition()
        .duration(300)
        .attr('opacity', 0)
        .remove();

      certainLabel
        .transition()
        .delay(200)
        .duration(400)
        .attr('opacity', 1);

      // Final halo pulse
      halo
        .transition()
        .duration(400)
        .attr('r', 42)
        .attr('stroke-width', 3)
        .attr('opacity', 0.8)
        .transition()
        .duration(300)
        .attr('r', 38)
        .attr('stroke-width', 2)
        .attr('opacity', 0.6);

      haloGlow
        .transition()
        .duration(400)
        .attr('r', 42)
        .attr('opacity', 0.1)
        .transition()
        .duration(300)
        .attr('r', 38)
        .attr('opacity', 0.06);
    }, 2800);

    return () => {
      stopped = true;
      cancelAnimationFrame(animFrameId);
      t0.stop();
      t1.stop();
      t2.stop();
      svg.selectAll('*').remove();
    };
  }, []);

  return (
    <div className="viz-container">
      <h4 className="viz-container__title">
        <i className="fa-solid fa-scale-balanced" /> Balance &mdash; Stability to Confidence
      </h4>
      <svg ref={svgRef} viewBox="0 0 280 180" />
      <p className="viz-container__caption">
        Bodily stability becomes mental certainty.
      </p>
    </div>
  );
}
